import { appStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { CommonErrorCode, GroupErrorCode, UserErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { IGroupInfo } from '../models/IGroupInfo';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import {
  ContentActionNotification,
  IMultipleResultNotificationOptions,
  MultipleResultNotificationOptions,
} from './ContentActionNotification';
import { ToasterService, ToastType } from './ToasterService';

// Notification For:
// => deleting groups

export class DeleteGroupsActionNotification {

  public static $inject = [
    '$state',
    '$translate',
    'ContentActionNotification'];

  constructor(
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private contentActionNotification: ContentActionNotification) { }

  public notify(
    result: IResult,
    groups: Set<IGroupInfo>) {

    // create a notification object for result reporting
    const notification: IMultipleResultNotificationOptions = MultipleResultNotificationOptions.create(
      new MultipleItemResult(groups, result),
      {
        describeSuccessSummary: (successCount: number) => {
          return this.$translate.instant(
            'deleteGroupsSuccess',
            { ITEM_COUNT: successCount, ITEM_NAME: L10n.elideIfLong(groups.first().name) }, L10n.Interpolations.Count);
        },
        describeErrorSummary: (errorCount: number) => {
          return this.$translate.instant(
            'deleteGroupsError',
            { ERROR_COUNT: errorCount, ITEM_NAME: L10n.elideIfLong(groups.first().name) }, L10n.Interpolations.Count);
        },
        describeErrorDetail: (errorItem: IErrorItem<IGroupInfo>) => {
          const siteUrls = siteUrlsSelector(appStore.getState());
          return {
            itemName: errorItem.item.name,
            itemUrl: siteUrls.groupUrl(errorItem.item.id),
            errorMessage: this.errorMessageFor(errorItem.error, errorItem.item.name)
          };
        }
      }
    );

    // pass the notification object to the service for displaying
    this.contentActionNotification.notify(notification);
  }

  private errorMessageFor(error: IError, groupName: string) {
    switch (error.code) {
      case CommonErrorCode.GroupNotFound:
        return this.$translate.instant('errorGroupNotFound', { ITEM_NAME: L10n.elideIfLong(groupName) });
      case GroupErrorCode.GroupCannotBeDeleted:
        return this.$translate.instant('errorUnableToDeleteAllUsersGroup', { GROUP_NAME: L10n.elideIfLong(groupName) });
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('DeleteGroupsActionNotification', DeleteGroupsActionNotification);


// Notification For:
// => renaming a group

export class RenameGroupActionNotification {

  public static $inject = [
    '$translate',
    'ToasterService'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private toaster: ToasterService) { }

  public notify(
    showErrorMsgFunc: (errorMsg: string) => void,
    result: IResult,
    newGroupName: string,
    oldGroupName: string) {

    if (!!result.errors && result.errors.length > 0) {
      const errorMsg: string = this.errorMessageFor(result.errors[0], newGroupName, oldGroupName);
      showErrorMsgFunc(errorMsg);
    } else {
      const successMsg = this.$translate.instant('updateGroupNameSuccessMsg', {
        NEW_ITEM_NAME: L10n.elideIfLong(newGroupName),
        ITEM_NAME: L10n.elideIfLong(oldGroupName)
      });
      this.toaster.pop({ type: ToastType.Info, text: successMsg});
    }
  }

  private errorMessageFor(error: IError, newGroupName: string, oldGroupName: string) {
    switch (error.code) {
      case UserErrorCode.UserGroupInvalidName:
        return this.$translate.instant('errorUserGroupInvalidName', { NEW_ITEM_NAME: L10n.elideIfLong(newGroupName) });
      case UserErrorCode.UserGroupWithNameAlreadyExists:
        return this.$translate.instant('errorUserGroupWithNameAlreadyExists', { NEW_ITEM_NAME: L10n.elideIfLong(newGroupName) });
      case UserErrorCode.UserAllUsersGroupAlreadyExists:
        return this.$translate.instant('errorUserAllUsersGroupAlreadyExists', { NEW_ITEM_NAME: L10n.elideIfLong(newGroupName) });
      case CommonErrorCode.GroupNotFound:
        return this.$translate.instant('errorGroupNotFound', { ITEM_NAME: L10n.elideIfLong(oldGroupName) });
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('RenameGroupActionNotification', RenameGroupActionNotification);


// Notification For:
// => creating a group

export class CreateGroupActionNotification {

  public static $inject = [
    '$translate',
    'ToasterService'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private toaster: ToasterService) { }

  public notify(
    showErrorMsgFunc: (errorMsg: string) => void,
    result: IResult,
    groupName: string) {

    if (!!result.errors && result.errors.length > 0) {
      const errorMsg: string = this.errorMessageFor(result.errors[0], groupName);
      showErrorMsgFunc(errorMsg);
    } else {
      const successMsg = this.$translate.instant('createGroupSuccessMsg', { NEW_ITEM_NAME: L10n.elideIfLong(groupName) });
      this.toaster.pop({ type: ToastType.Info, text: successMsg });
    }
  }

  private errorMessageFor(error: IError, groupName: string) {
    switch (error.code) {
      case UserErrorCode.UserGroupInvalidName:
        return this.$translate.instant('errorUserGroupInvalidName', { NEW_ITEM_NAME: L10n.elideIfLong(groupName) });
      case UserErrorCode.UserGroupWithNameAlreadyExists:
        return this.$translate.instant('errorUserGroupWithNameAlreadyExists', { NEW_ITEM_NAME: L10n.elideIfLong(groupName) });
      case UserErrorCode.UserAllUsersGroupAlreadyExists:
        return this.$translate.instant('errorUserAllUsersGroupAlreadyExists', { NEW_ITEM_NAME: L10n.elideIfLong(groupName) });
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('CreateGroupActionNotification', CreateGroupActionNotification);
